import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { t } from '@/i18n';
import type { DashboardResponse } from '@ip/shared-types';
import { CostsByPaymentChart } from './CostsByPaymentChart';
import { CostsByTagChart } from './CostsByTagChart';
import { MonthlyCostsChart } from './MonthlyCostsChart';
import { SummaryCards } from './SummaryCards';
import { UnpaidChart } from './UnpaidChart';

/**
 * The cost-side detail charts (by tag, by payment method, unpaid) collapsed
 * behind a disclosure. They are supporting evidence, not headline health — on
 * the page they sit under "Kondycja spółki" but folded, so the section leads
 * with the KPIs and the year charts instead of twelve equal-weight cards.
 */
export function CostDetails({ data }: { data: DashboardResponse }) {
  const [open, setOpen] = useState(false);

  const topPaymentMethod = (() => {
    const totals = new Map<string, number>();
    for (const row of data.monthlyPaymentMethodCosts) {
      totals.set(row.label, (totals.get(row.label) || 0) + row.totalGross);
    }
    let best: string | undefined;
    let bestVal = 0;
    for (const [label, val] of totals) {
      if (val > bestVal) {
        best = label;
        bestVal = val;
      }
    }
    return best;
  })();

  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const currentMonthData = data.monthlyCosts.find((m) => m.month === currentMonthKey);
  const currentMonthUnpaid =
    data.unpaidByMonth.find((m) => m.month === currentMonthKey)?.unpaidGross || 0;

  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="text-muted-foreground hover:text-foreground hover:bg-muted/50 flex w-fit items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors"
      >
        <ChevronDown
          aria-hidden="true"
          className={`size-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
        />
        {open ? t('dashboard.financial.hideCostDetails') : t('dashboard.financial.showCostDetails')}
      </button>

      {open && (
        <div className="flex flex-col gap-4">
          <SummaryCards
            summary={data.summary}
            topPaymentMethod={topPaymentMethod}
            currentMonthGross={currentMonthData?.totalGross || 0}
            currentMonthTax={currentMonthData?.totalTax || 0}
            currentMonthUnpaid={currentMonthUnpaid}
            year={data.year}
          />
          <MonthlyCostsChart data={data.monthlyCosts} year={data.year} />
          <CostsByTagChart data={data.monthlyTagCosts} year={data.year} />
          <div className="grid gap-4 xl:grid-cols-2">
            <CostsByPaymentChart data={data.monthlyPaymentMethodCosts} year={data.year} />
            <UnpaidChart data={data.unpaidByMonth} year={data.year} />
          </div>
        </div>
      )}
    </div>
  );
}
